import { ChevronDown } from 'lucide-react';

import { cn } from '../utils';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './dropdown-menu';

export interface TableFilterOption {
  value: string;
  label: string;
}

export interface TableFilterProps {
  /** Filter name, e.g. "Last active". */
  label: string;
  options: TableFilterOption[];
  /** Currently selected option value. */
  value: string;
  /** Option value considered "off" — the control looks inactive on it. */
  defaultValue: string;
  onChange: (value: string) => void;
  className?: string;
}

/**
 * A named, preset filter rendered as a labelled dropdown of radio options. When
 * the selection is off its default the trigger switches to an "active" look and
 * shows the chosen option's label. Labels are supplied by the caller — this
 * package owns no translations.
 */
export function TableFilter({
  label,
  options,
  value,
  defaultValue,
  onChange,
  className,
}: TableFilterProps) {
  const active = value !== defaultValue;
  const selected = options.find((o) => o.value === value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(active && 'border-primary text-primary', className)}
        >
          <span className="text-muted-foreground">{label}:</span>
          <span className="font-medium">{selected?.label ?? label}</span>
          <ChevronDown className="size-4 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-48">
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
